import React from "react";
import { I18n } from "react-redux-i18n";
import cx from "classnames";

import { Loading } from "@common/components/loading";
import { getTestAttributes } from "@common/utils/tests";

import { FieldsProps } from "./fields.types";
import { Field } from "./components/field";
import { InternalFieldPrimaryList } from "../../spreadsheet-management.types";

import "./fields.styles.scss";

export const Fields: React.FC<FieldsProps> = (props) => {
  const { fields, isFieldsFromSearch, loading, className, selection, itemUniqProperty, onDrag, id, getId, onDoubleClick } = props;

  const handleRowClickUp = React.useCallback(
    ({ item, event }: { item: InternalFieldPrimaryList; event: React.MouseEvent<HTMLDivElement> }) => {
      const alreadySelectedItems = selection?.items || ([] as InternalFieldPrimaryList[]);
      let newSelectedItems = [item];

      if (!(event.ctrlKey || event.shiftKey) && alreadySelectedItems.length > 1) {
        if (selection?.onChange) {
          selection.onChange(newSelectedItems);
        }
      }
    },
    [selection]
  );

  const handleRowClickDown = React.useCallback(
    ({ item, event }: { item: InternalFieldPrimaryList; event: React.MouseEvent<HTMLDivElement> }) => {
      const alreadySelectedItems = selection?.items || ([] as InternalFieldPrimaryList[]);
      let newSelectedItems = [item];
      const isAlreadySelected = alreadySelectedItems.some((x) => x[itemUniqProperty] === item[itemUniqProperty]);

      if (!(event.ctrlKey || event.shiftKey) && alreadySelectedItems.length > 1) {
        return;
      } else if ((event.ctrlKey || event.shiftKey) && selection?.type === "multiple") {
        newSelectedItems = isAlreadySelected
          ? alreadySelectedItems.filter((x) => x[itemUniqProperty] !== item[itemUniqProperty])
          : alreadySelectedItems.concat(item);
      } else if (alreadySelectedItems.length === 1 && isAlreadySelected) {
        return;
      }

      if (selection?.onChange) {
        selection.onChange(newSelectedItems);
      }
    },
    [selection, itemUniqProperty]
  );

  const handleRowDoubleClick = React.useCallback(
    ({ item, event }: { item: InternalFieldPrimaryList; event: React.MouseEvent<HTMLDivElement> }) => {
      if (onDoubleClick) {
        onDoubleClick({ item, event });
      }
    },
    [onDoubleClick]
  );

  const isRowSelected = React.useCallback(
    (item: InternalFieldPrimaryList) => {
      return selection?.items.some((x) => x[itemUniqProperty] === item[itemUniqProperty]) ?? false;
    },
    [selection?.items, itemUniqProperty]
  );

  return (
    <div className={cx("SpreadsheetDetailsSpreadsheetManagementFields", className)} {...getTestAttributes({ id })}>
      {loading && <Loading size={20} margin="20px" />}
      {!loading && !fields.length && (
        <div className="SpreadsheetDetailsSpreadsheetManagementFields__no-data-message">{I18n.t("No data")}</div>
      )}
      {!loading && fields.length > 0 && (
        <div className="SpreadsheetDetailsSpreadsheetManagementFields__list">
          {fields.map((field, rowIndex) => (
            <Field
              key={field[itemUniqProperty]?.toString()}
              field={field}
              isFieldsFromSearch={isFieldsFromSearch}
              onMouseUp={handleRowClickUp}
              onMouseDown={handleRowClickDown}
              onDoubleClick={handleRowDoubleClick}
              isSelected={isRowSelected(field)}
              onDrag={onDrag}
              getId={{
                rowId: getId.rowId({ rowIndex }),
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
